import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Star, User, Quote } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";

interface RatingWithReviewer {
  id: string;
  rating: number;
  written_feedback: string | null;
  created_at: string;
  reviewer_name: string;
  reviewer_user_type: string;
  job_title: string;
}

interface CustomerReviewsDialogProps {
  customerId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CustomerReviewsDialog = ({ customerId, open, onOpenChange }: CustomerReviewsDialogProps) => {
  const [ratings, setRatings] = useState<RatingWithReviewer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<{
    full_name: string;
    average_rating: number;
    total_reviews: number;
  } | null>(null);

  useEffect(() => {
    if (open && customerId) {
      fetchRatings();
      fetchCustomerInfo();
    }
  }, [open, customerId]);

  const fetchCustomerInfo = async () => {
    if (!customerId) return;

    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name, average_rating, total_reviews")
        .eq("id", customerId)
        .single();
      
      if (profile) {
        setCustomerInfo({
          full_name: profile.full_name || "Customer",
          average_rating: profile.average_rating || 0,
          total_reviews: profile.total_reviews || 0
        });
      }
    } catch (error) {
      console.error("Error fetching customer info:", error);
    }
  };

  const fetchRatings = async () => {
    if (!customerId) return;

    setIsLoading(true);
    try {
      const { data: ratingsData, error } = await supabase
        .from("ratings")
        .select(`
          id,
          rating,
          written_feedback,
          created_at,
          job_id,
          reviewer_id
        `)
        .eq("reviewee_id", customerId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (ratingsData && ratingsData.length > 0) {
        const ratingsWithReviewers = await Promise.all(
          ratingsData.map(async (rating) => {
            try {
              const { data: reviewer } = await supabase
                .from("profiles")
                .select("full_name, email, user_type")
                .eq("id", rating.reviewer_id)
                .single();

              const { data: job } = await supabase
                .from("jobs")
                .select("title")
                .eq("id", rating.job_id)
                .single();

              return {
                ...rating,
                reviewer_name: reviewer?.full_name || reviewer?.email || "Anonymous",
                reviewer_user_type: reviewer?.user_type || "tradesman",
                job_title: job?.title || "Job"
              };
            } catch (e) {
              console.warn("Error fetching reviewer for rating:", rating.id, e);
              return {
                ...rating,
                reviewer_name: "Anonymous",
                reviewer_user_type: "tradesman",
                job_title: "Job"
              };
            }
          })
        );

        setRatings(ratingsWithReviewers);
      } else {
        setRatings([]);
      }
    } catch (error) {
      console.error("Error fetching ratings:", error);
      setRatings([]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-400" />
            Customer Reviews
          </DialogTitle>
        </DialogHeader>

        {customerInfo && (
          <div className="bg-muted/50 rounded-lg p-4 mb-2">
            <h3 className="font-semibold text-lg mb-2">{customerInfo.full_name}</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {renderStars(Math.round(customerInfo.average_rating))}
                <span className="font-medium">{customerInfo.average_rating.toFixed(1)}</span>
              </div>
              <Badge variant="outline">
                {customerInfo.total_reviews} review{customerInfo.total_reviews !== 1 ? 's' : ''}
              </Badge>
            </div>
          </div>
        )}

        <ScrollArea className="flex-1">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-sm text-muted-foreground">Loading reviews...</p>
            </div>
          ) : ratings.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Star className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
              <p className="text-base font-medium">No customer reviews yet</p>
            </div>
          ) : (
            <div className="space-y-4 pr-4">
              {ratings.map((rating) => (
                <div key={rating.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                        <User className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">{rating.reviewer_name}</p>
                        <p className="text-sm text-muted-foreground">
                          Job: {rating.job_title}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {renderStars(rating.rating)}
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(rating.created_at)}
                      </p>
                    </div>
                  </div>

                  {rating.written_feedback && (
                    <div className="bg-muted/30 rounded-lg p-3 relative">
                      <Quote className="h-4 w-4 text-muted-foreground absolute top-2 left-2 opacity-50" />
                      <p className="text-sm pl-6 italic leading-relaxed">{rating.written_feedback}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
